import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  Query,
  QuerySnapshot,
  runQuery,
  type Entry,
} from './refs'
import { newId } from './ulid'
import { applyOp, clone, deepEqual, DEL_KEY, isPlainObject, splitPath } from './util'
import type {
  ApplyRemoteOpts,
  CommitTx,
  Data,
  DocRow,
  Driver,
  FailedOp,
  Identity,
  Op,
  PathMapper,
  RemoteChange,
} from './types'
import type { TabCoordinator } from './tabs'

export const LAZY_COLLECTIONS = ['chunks']
export const LOCAL_ONLY: string[] = []
export const MAX_BATCH_OPS = 200

// Server-stamped fields: a replace that omits them must not turn into a field delete.
const SERVER_FIELDS = new Set(['orgId', 'ownerUid', 'createdAt', 'updatedAt', 'updatedBy', 'syncedAt'])

const KV_IDENTITY = 'identity'
const KV_CURSOR = 'cursor'
const KV_EPOCH = 'resyncEpoch'
// Several tabs on one database (docs/SYNC.md §2): the newest generation, and one change record per generation.
const KV_GEN = 'xtab:gen'
const genKey = (g: number) => `xtab:${g}`
const GEN_KEEP = 500

export interface StoreOptions extends Identity {
  driver: Driver
  paths: PathMapper
  localOnly?: string[]
  now?: () => number
  /** Other tabs share this database (web). Absent = the only writer, as on the phone. */
  tabs?: TabCoordinator
}

/** What one write() queued: the outbox group and its op ids (empty when nothing changed or all local-only). */
export interface WriteReceipt { groupId: string; opIds: string[] }

export type WriteItem =
  | { kind: 'set'; path: string; data: Data; merge: boolean }
  | { kind: 'update'; path: string; data: Data }
  | { kind: 'delete'; path: string }

interface DocState {
  server: Data | null
  view: Data | null
}

export type SnapshotTarget = DocumentReference | CollectionReference | Query
type Next = (snap: never) => void

interface Listener {
  target: SnapshotTarget
  next: (snap: DocumentSnapshot | QuerySnapshot) => void
  error?: (err: unknown) => void
  active: boolean
  fired: boolean
  last: unknown
  chain: Promise<void>
}

const EMPTY: DocState = { server: null, view: null }

function collectionOf(path: string): string {
  return path.slice(0, path.lastIndexOf('/'))
}

function lastSegment(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

function checkData(data: unknown, what: string): Data {
  if (!isPlainObject(data)) throw new Error(`${what}: data must be a plain object`)
  return clone(data)
}

export class Store {
  readonly orgId: string
  readonly uid: string
  private readonly driver: Driver
  private readonly paths: PathMapper
  private readonly localOnly: Set<string>
  private readonly now: () => number

  private docs = new Map<string, DocState>()
  private byCollection = new Map<string, Set<string>>()
  private pending: Op[] = []
  // Groups written with { coalesce: true } this session — only these may be folded into a later write.
  private coalescable = new Set<string>()
  private failedCount = 0
  private listeners = new Set<Listener>()
  private outboxListeners = new Set<() => void>()
  private queue: Promise<unknown> = Promise.resolve()
  private closed = false
  private writeErrorListeners = new Set<(err: unknown) => void>()
  private readonly tabs: TabCoordinator | undefined
  private gen = 0                      // the last generation this tab has caught up to
  private unsubTabs: (() => void) | null = null

  constructor(opts: StoreOptions) {
    this.orgId = opts.orgId
    this.uid = opts.uid
    this.driver = opts.driver
    this.paths = opts.paths
    this.localOnly = new Set(opts.localOnly ?? LOCAL_ONLY)
    this.now = opts.now ?? Date.now
    this.tabs = opts.tabs
  }

  async open(): Promise<this> {
    await this.driver.init()
    const identity = `${this.orgId}\n${this.uid}`
    if ((await this.driver.kvGet(KV_IDENTITY)) !== identity) {
      await this.driver.wipe()
      await this.driver.commit({ putDocs: [], deleteDocs: [], appendOutbox: [], ackOutbox: [], kv: { [KV_IDENTITY]: identity } })
    }
    for (const row of await this.driver.loadAll({ excludeCollections: LAZY_COLLECTIONS })) {
      this.putMemory(row.path, { server: row.server, view: row.view })
    }
    this.pending = await this.driver.readOutbox(Number.MAX_SAFE_INTEGER)
    this.failedCount = (await this.driver.outboxCount()).failed
    if (this.tabs) {
      this.gen = Number((await this.driver.kvGet(KV_GEN)) ?? 0)
      // A nudge from another tab: catch up now so open screens refresh (a hint only — writes catch up anyway).
      this.unsubTabs = this.tabs.subscribe((msg) => {
        if (this.closed || !(Number(msg?.gen) > this.gen)) return
        this.local(() => this.catchUp()).catch(() => undefined)
      })
    }
    return this
  }

  /** Finish every queued write, then release the database (an update restart must not leave a write open). */
  async shutdown(): Promise<void> {
    // Saves already started (setDoc awaits the store first) join the queue a tick later — let them.
    await new Promise((r) => setTimeout(r, 0))
    let q: Promise<unknown>
    do { q = this.queue; await q } while (q !== this.queue)
    this.close()
    await this.driver.close?.()
  }

  /** Called when a local save fails — the app shows it instead of silently snapping back. */
  onWriteError(cb: (err: unknown) => void): () => void {
    this.writeErrorListeners.add(cb)
    return () => this.writeErrorListeners.delete(cb)
  }

  close(): void {
    this.closed = true
    this.unsubTabs?.()
    this.unsubTabs = null
    for (const l of this.listeners) l.active = false
    this.listeners.clear()
    this.outboxListeners.clear()
  }

  // ---- reads ----

  async getDoc(ref: DocumentReference): Promise<DocumentSnapshot> {
    this.assertMapped(ref.path)
    return new DocumentSnapshot(ref, (await this.readStates([ref.path])).get(ref.path)?.view ?? null)
  }

  async getDocs(target: CollectionReference | Query): Promise<QuerySnapshot> {
    return new QuerySnapshot(await this.runTarget(target))
  }

  onSnapshot(target: SnapshotTarget, next: Next, error?: (err: unknown) => void): () => void {
    const l: Listener = {
      target,
      next: next as Listener['next'],
      error,
      active: true,
      fired: false,
      last: undefined,
      chain: Promise.resolve(),
    }
    this.listeners.add(l)
    this.emit(l)
    return () => {
      l.active = false
      this.listeners.delete(l)
    }
  }

  // ---- writes ----

  /**
   * `coalesce`: fold earlier unsent coalescable groups into this one when this write covers every path
   * they touched (autosave: one queued copy of a note, not fifty). A group is only moved later when no
   * other pending write touches its paths in between, so the order of writes to any doc never changes.
   */
  write(items: WriteItem[], opts: { coalesce?: boolean } = {}): Promise<WriteReceipt> {
    if (items.length > MAX_BATCH_OPS) {
      return Promise.reject(new Error(`A batch holds at most ${MAX_BATCH_OPS} writes (got ${items.length})`))
    }
    const done = this.exclusive(async () => {
      const work = await this.readStates(items.map((i) => i.path))
      const before = new Map(work)
      const touched = new Set<string>()
      const ops: Op[] = []
      const groupId = newId()
      const clientTs = this.now()

      for (const item of items) {
        const local = this.isLocalOnly(item.path)
        const remote = local ? item.path : this.paths.toRemote(item.path, this.identity())
        const cur = work.get(item.path) ?? EMPTY
        const partial = this.toOp(item, cur.view)
        if (!partial) continue
        const view = applyOp(cur.view, partial)
        work.set(item.path, { server: local ? view : cur.server, view })
        touched.add(item.path)
        if (!local) ops.push({ ...partial, opId: newId(), groupId, path: remote, clientTs })
      }
      if (touched.size === 0) return { groupId, opIds: [] }

      const superseded = opts.coalesce ? this.foldInto(ops) : []
      const tx = this.emptyTx()
      tx.appendOutbox = ops
      tx.ackOutbox = superseded.map((o) => o.opId)
      const changed = this.stage(tx, before, work, touched)
      await this.commitTx(tx)
      if (superseded.length) {
        const gone = new Set(tx.ackOutbox)
        this.pending = this.pending.filter((o) => !gone.has(o.opId))
        for (const o of superseded) this.coalescable.delete(o.groupId)
      }
      if (opts.coalesce && ops.length) this.coalescable.add(groupId)
      this.pending.push(...ops)
      this.land(work, touched)
      this.notify(changed)
      if (ops.length) for (const cb of [...this.outboxListeners]) cb()
      return { groupId, opIds: ops.map((o) => o.opId) }
    })
    done.catch((err) => { if (!this.closed) for (const cb of [...this.writeErrorListeners]) { try { cb(err) } catch { /* listener */ } } })
    return done
  }

  /** Merge superseded pending ops into `ops` (mutated) and return the superseded ones. */
  private foldInto(ops: Op[]): Op[] {
    if (!ops.length || ops.some((o) => o.mode === 'delete')) return []
    const newPaths = new Set(ops.map((o) => o.path))
    const groups: Op[][] = []
    for (const op of this.pending) {
      const g = groups[groups.length - 1]
      if (g && g[0]!.groupId === op.groupId) g.push(op)
      else groups.push([op])
    }
    const take: Op[][] = []
    for (let i = groups.length - 1; i >= 0; i--) {
      const g = groups[i]!
      if (!g.some((o) => newPaths.has(o.path))) continue
      const ok = this.coalescable.has(g[0]!.groupId) && g.every((o) => newPaths.has(o.path) && o.mode !== 'delete')
      if (!ok) break
      take.unshift(g)
    }
    if (!take.length) return []
    const folded = new Set<string>()           // old data goes into the first new op per path only
    for (const op of ops) {
      if (folded.has(op.path)) continue
      folded.add(op.path)
      let mode = op.mode
      let data: Data | null = null
      for (const g of take) {
        for (const o of g) {
          if (o.path !== op.path) continue
          if (data === null || o.mode === 'set') { mode = o.mode; data = { ...(o.data ?? {}) } }
          else data = { ...(data as Data), ...(o.data ?? {}) }
        }
      }
      if (data === null) continue
      if (op.mode === 'set') { mode = 'set'; data = { ...(op.data ?? {}) } }
      else data = { ...data, ...(op.data ?? {}) }
      op.mode = mode
      op.data = data
    }
    return take.flat()
  }

  // ---- engine-facing ----

  // Oldest first, never splitting a group; always at least one whole group when any is pending.
  async readOutbox(limit: number): Promise<Op[]> {
    if (this.tabs && !this.closed) await this.local(() => this.catchUp()).catch(() => undefined)
    const out: Op[] = []
    let i = 0
    while (i < this.pending.length) {
      const groupId = this.pending[i]!.groupId
      let j = i
      while (j < this.pending.length && this.pending[j]!.groupId === groupId) j++
      if (out.length > 0 && out.length + (j - i) > limit) break
      out.push(...this.pending.slice(i, j))
      i = j
      if (out.length >= limit) break
    }
    return clone(out)
  }

  readFailed(): Promise<FailedOp[]> {
    return this.driver.readFailed()
  }

  counts(): { pending: number; failed: number } {
    return { pending: this.pending.length, failed: this.failedCount }
  }

  onOutboxChange(cb: () => void): () => void {
    this.outboxListeners.add(cb)
    return () => this.outboxListeners.delete(cb)
  }

  // Acked ops fold into `server` (the view does not move); failed ops drop out of the view.
  resolveOps(res: { acks: string[]; fails: { opId: string; error: string }[] }): Promise<void> {
    return this.exclusive(async () => {
      const acks = new Set(res.acks)
      const fails = new Map(res.fails.map((f) => [f.opId, f.error]))
      const hit = this.pending.filter((op) => acks.has(op.opId) || fails.has(op.opId))
      if (hit.length === 0) return

      const logical = new Map<string, string>()
      for (const op of hit) {
        const p = this.paths.toLogical(op.path, this.identity())
        if (p) logical.set(op.path, p)
      }
      const work = await this.readStates([...logical.values()])
      const before = new Map(work)
      const remaining = this.pending.filter((op) => !acks.has(op.opId) && !fails.has(op.opId))

      for (const op of hit) {
        const p = logical.get(op.path)
        if (!p || !acks.has(op.opId)) continue
        const cur = work.get(p) ?? EMPTY
        work.set(p, { server: applyOp(cur.server, op), view: cur.view })
      }
      const touched = new Set(logical.values())
      for (const p of touched) this.recompute(work, p, remaining)

      const tx = this.emptyTx()
      tx.ackOutbox = hit.filter((op) => acks.has(op.opId)).map((op) => op.opId)
      tx.failOutbox = hit.filter((op) => fails.has(op.opId)).map((op) => ({ opId: op.opId, error: fails.get(op.opId)! }))
      const changed = this.stage(tx, before, work, touched)
      await this.commitTx(tx)
      this.pending = remaining
      this.failedCount += tx.failOutbox.length
      this.land(work, touched)
      this.notify(changed)
    })
  }

  applyRemote(changes: RemoteChange[], opts: ApplyRemoteOpts = {}): Promise<void> {
    return this.exclusive(async () => {
      const incoming = new Map<string, Data | null>()
      for (const c of changes) {
        const p = this.paths.toLogical(c.path, this.identity())
        if (p) incoming.set(p, c.data === null ? null : clone(c.data))
      }
      const prunePaths: string[] = []
      if (opts.prune) {
        // A resync is rare, so reading every row once (lazy collections included) is fine.
        for (const row of await this.driver.loadAll({ excludeCollections: [] })) {
          if (row.server === null || incoming.has(row.path) || this.isLocalOnly(row.path)) continue
          const remote = this.paths.toRemote(row.path, this.identity())
          if (!opts.prune.keep.has(remote) && !opts.prune.skip?.(remote)) prunePaths.push(row.path)
        }
      }
      const work = await this.readStates([...incoming.keys(), ...prunePaths])
      const before = new Map(work)
      for (const [p, data] of incoming) work.set(p, { server: data, view: (work.get(p) ?? EMPTY).view })
      for (const p of prunePaths) work.set(p, { server: null, view: work.get(p)!.view })
      const touched = new Set([...incoming.keys(), ...prunePaths])
      for (const p of touched) this.recompute(work, p, this.pending)

      const tx = this.emptyTx()
      if (opts.cursor !== undefined) tx.kv = { [KV_CURSOR]: String(opts.cursor) }
      const changed = this.stage(tx, before, work, touched)
      if (changed.size === 0 && !tx.kv) return
      await this.commitTx(tx)
      this.land(work, touched)
      this.notify(changed)
    })
  }

  async getCursor(): Promise<number | null> {
    const v = await this.driver.kvGet(KV_CURSOR)
    return v === null ? null : Number(v)
  }

  /** Forget the pull cursor once per `epoch` so the next pull is a full snapshot — repairs a store that missed
   *  changes (e.g. a collection the server didn't publish yet when they happened). Docs and the outbox stay; the
   *  snapshot overwrites server state and prunes what the server no longer has. True when it reset. */
  resyncOnce(epoch: number): Promise<boolean> {
    return this.exclusive(async () => {
      const seen = Number((await this.driver.kvGet(KV_EPOCH)) ?? 0)
      if (seen >= epoch) return false
      const tx = this.emptyTx()
      tx.kv = { [KV_CURSOR]: null, [KV_EPOCH]: String(epoch) }
      await this.driver.commit(tx)
      return true
    })
  }

  // Drops failed ops for good (after the user has seen them). No ids = all.
  discardFailed(opIds?: string[]): Promise<void> {
    return this.exclusive(async () => {
      const ids = opIds ?? (await this.driver.readFailed()).map((f) => f.opId)
      if (ids.length === 0) return
      const tx = this.emptyTx()
      tx.ackOutbox = ids
      await this.commitTx(tx)
      this.failedCount = (await this.driver.outboxCount()).failed
    })
  }

  kvGet(key: string): Promise<string | null> {
    return this.driver.kvGet(`app:${key}`)
  }

  kvSet(key: string, value: string | null): Promise<void> {
    return this.exclusive(async () => {
      const tx = this.emptyTx()
      tx.kv = { [`app:${key}`]: value }
      await this.driver.commit(tx)
    })
  }

  // ---- internals ----

  private identity(): Identity {
    return { orgId: this.orgId, uid: this.uid }
  }

  private emptyTx(): CommitTx & { failOutbox: { opId: string; error: string }[] } {
    return { putDocs: [], deleteDocs: [], appendOutbox: [], ackOutbox: [], failOutbox: [] }
  }

  /** Store work that commits: this tab's queue, and with other tabs, their write lock + a catch-up first. */
  private exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const tabs = this.tabs
    if (!tabs) return this.local(fn)
    return this.local(() => tabs.lock(async () => {
      if (this.closed) throw new Error('localstore: store is closed')
      await this.catchUp()
      return fn()
    }))
  }

  /** This tab's queue only (no cross-tab lock): catch-ups, which only read committed rows. */
  private local<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.queue.then(() => {
      if (this.closed) throw new Error('localstore: store is closed')
      return fn()
    })
    this.queue = run.catch(() => undefined)
    return run
  }

  /** Commit a tx; with other tabs, add this generation's change record to the same transaction and nudge them. */
  private async commitTx(tx: CommitTx): Promise<void> {
    if (!this.tabs) { await this.driver.commit(tx); return }
    const paths = [...tx.putDocs.map((r) => r.path), ...tx.deleteDocs]
    const outbox = tx.appendOutbox.length > 0 || tx.ackOutbox.length > 0 || (tx.failOutbox?.length ?? 0) > 0
    if (!paths.length && !outbox) { await this.driver.commit(tx); return }
    const gen = this.gen + 1
    tx.kv = { ...(tx.kv ?? {}), [KV_GEN]: String(gen), [genKey(gen)]: JSON.stringify({ paths, outbox }) }
    if (gen > GEN_KEEP) tx.kv[genKey(gen - GEN_KEEP)] = null
    await this.driver.commit(tx)
    this.gen = gen
    try { this.tabs.publish({ gen }) } catch { /* a closed channel: the next write catches up anyway */ }
  }

  /** Load what other tabs committed since this tab last looked (docs/SYNC.md §2, several tabs). */
  private async catchUp(): Promise<void> {
    if (!this.tabs) return
    const head = Number((await this.driver.kvGet(KV_GEN)) ?? 0)
    if (head <= this.gen) return
    const paths = new Set<string>()
    let outbox = false
    let full = head - this.gen > GEN_KEEP
    for (let g = this.gen + 1; !full && g <= head; g++) {
      const raw = await this.driver.kvGet(genKey(g))
      if (raw === null) { full = true; break }
      const rec = JSON.parse(raw) as { paths: string[]; outbox: boolean }
      for (const p of rec.paths) paths.add(p)
      if (rec.outbox) outbox = true
    }
    const changed = new Set<string>()
    if (full) {
      const rows = await this.driver.loadAll({ excludeCollections: LAZY_COLLECTIONS })
      const seen = new Set<string>()
      for (const row of rows) {
        seen.add(row.path)
        if (!deepEqual(this.docs.get(row.path)?.view ?? null, row.view)) changed.add(row.path)
        this.putMemory(row.path, { server: row.server, view: row.view })
      }
      for (const p of [...this.docs.keys()]) {
        if (seen.has(p)) continue
        if (this.docs.get(p)?.view) changed.add(p)
        this.putMemory(p, EMPTY)
      }
      outbox = true
    } else if (paths.size) {
      const rows = new Map((await this.driver.loadDocs([...paths])).map((r) => [r.path, r]))
      for (const p of paths) {
        const row = rows.get(p)
        const next: DocState = row ? { server: row.server, view: row.view } : EMPTY
        if (this.isLazy(collectionOf(p))) { changed.add(p); continue }
        if (!deepEqual(this.docs.get(p)?.view ?? null, next.view)) changed.add(p)
        this.putMemory(p, next)
      }
    }
    this.gen = head
    if (outbox) {
      this.pending = await this.driver.readOutbox(Number.MAX_SAFE_INTEGER)
      this.failedCount = (await this.driver.outboxCount()).failed
      if (this.pending.length) for (const cb of [...this.outboxListeners]) cb()
    }
    this.notify(changed)
  }

  private isLocalOnly(path: string): boolean {
    return this.localOnly.has(path.split('/')[0] ?? '')
  }

  private isLazy(collectionPath: string): boolean {
    return LAZY_COLLECTIONS.includes(lastSegment(collectionPath))
  }

  private assertMapped(path: string): void {
    if (!this.isLocalOnly(path)) this.paths.toRemote(path, this.identity())
  }

  private toOp(item: WriteItem, view: Data | null): Pick<Op, 'mode' | 'data'> | null {
    if (item.kind === 'delete') return view === null && this.isLocalOnly(item.path) ? null : { mode: 'delete', data: null }
    const data = checkData(item.data, item.kind === 'update' ? 'updateDoc' : 'setDoc')
    for (const k of Object.keys(data)) {
      if (isPlainObject(data[k]) && DEL_KEY in (data[k] as object)) throw new Error(`"${DEL_KEY}" is reserved`)
    }
    if (item.kind === 'update') {
      if (view === null) throw new Error(`updateDoc: no document at ${item.path}`)
      return Object.keys(data).length ? { mode: 'update', data } : null
    }
    if (item.merge) return Object.keys(data).length || view === null ? { mode: 'update', data } : null
    if (view === null) return { mode: 'set', data }

    // Replace of an existing doc travels as a field diff so it cannot clobber other devices' fields.
    const diff: Data = {}
    for (const k of Object.keys(data)) if (!deepEqual(data[k], view[k])) diff[k] = data[k]
    for (const k of Object.keys(view)) if (!(k in data) && !SERVER_FIELDS.has(k)) diff[k] = { [DEL_KEY]: true }
    return Object.keys(diff).length ? { mode: 'update', data: diff } : null
  }

  private recompute(work: Map<string, DocState>, path: string, pending: Op[]): void {
    const cur = work.get(path) ?? EMPTY
    let view = cur.server
    if (!this.isLocalOnly(path)) {
      const remote = this.paths.toRemote(path, this.identity())
      for (const op of pending) if (op.path === remote) view = applyOp(view, op)
    }
    work.set(path, { server: cur.server, view })
  }

  private async readStates(paths: string[]): Promise<Map<string, DocState>> {
    const out = new Map<string, DocState>()
    const lazy: string[] = []
    for (const p of new Set(paths)) {
      if (this.isLazy(collectionOf(p))) lazy.push(p)
      else out.set(p, this.docs.get(p) ?? EMPTY)
    }
    if (lazy.length) {
      for (const p of lazy) out.set(p, EMPTY)
      for (const row of await this.driver.loadDocs(lazy)) out.set(row.path, { server: row.server, view: row.view })
    }
    return out
  }

  // Fills the tx with rows that really changed; returns the paths whose *view* changed.
  private stage(tx: CommitTx, before: Map<string, DocState>, work: Map<string, DocState>, touched: Set<string>): Set<string> {
    const viewChanged = new Set<string>()
    for (const p of touched) {
      const next = { ...work.get(p)! }
      work.set(p, next)
      const prev = before.get(p) ?? EMPTY
      if (deepEqual(prev.view, next.view)) {
        next.view = prev.view
        if (deepEqual(prev.server, next.server)) continue
      } else {
        viewChanged.add(p)
      }
      if (next.server === null && next.view === null) tx.deleteDocs.push(p)
      else tx.putDocs.push({ path: p, server: next.server, view: next.view })
    }
    return viewChanged
  }

  private land(work: Map<string, DocState>, touched: Set<string>): void {
    for (const p of touched) {
      if (!this.isLazy(collectionOf(p))) this.putMemory(p, work.get(p)!)
    }
  }

  private putMemory(path: string, st: DocState): void {
    const col = collectionOf(path)
    if (st.server === null && st.view === null) {
      this.docs.delete(path)
      this.byCollection.get(col)?.delete(path)
      return
    }
    this.docs.set(path, st)
    let set = this.byCollection.get(col)
    if (!set) this.byCollection.set(col, (set = new Set()))
    set.add(path)
  }

  private async runTarget(target: CollectionReference | Query): Promise<Entry[]> {
    this.assertMapped(target.path)
    const entries: Entry[] = []
    if (this.isLazy(target.path)) {
      const { parent, collection } = splitPath(`${target.path}/_`)
      for (const row of await this.driver.loadCollection(parent, collection)) {
        if (row.view) entries.push({ path: row.path, id: lastSegment(row.path), data: row.view })
      }
    } else {
      for (const p of this.byCollection.get(target.path) ?? []) {
        const view = this.docs.get(p)?.view
        if (view) entries.push({ path: p, id: lastSegment(p), data: view })
      }
    }
    return runQuery(entries, target instanceof Query ? target.constraints : [])
  }

  private notify(changed: Set<string>): void {
    if (changed.size === 0) return
    const cols = new Set([...changed].map(collectionOf))
    for (const l of this.listeners) {
      const hit = l.target instanceof DocumentReference ? changed.has(l.target.path) : cols.has(l.target.path)
      if (hit) this.emit(l)
    }
  }

  private emit(l: Listener): void {
    l.chain = l.chain
      .then(async () => {
        if (!l.active) return
        if (l.target instanceof DocumentReference) {
          this.assertMapped(l.target.path)
          const view = (await this.readStates([l.target.path])).get(l.target.path)?.view ?? null
          if (!l.active || (l.fired && deepEqual(l.last, view))) return
          l.last = view
          l.fired = true
          this.deliver(l, new DocumentSnapshot(l.target, view))
          return
        }
        const entries = await this.runTarget(l.target)
        const prev = l.last as Entry[] | undefined
        const same =
          l.fired &&
          prev !== undefined &&
          prev.length === entries.length &&
          prev.every((e, i) => e.path === entries[i]!.path && deepEqual(e.data, entries[i]!.data))
        if (!l.active || same) return
        l.last = entries
        l.fired = true
        this.deliver(l, new QuerySnapshot(entries))
      })
      .catch((err) => {
        if (l.active && l.error) l.error(err)
      })
  }

  private deliver(l: Listener, snap: DocumentSnapshot | QuerySnapshot): void {
    try {
      l.next(snap)
    } catch (err) {
      console.error('localstore: onSnapshot listener threw', err)
    }
  }
}

export async function createStore(opts: StoreOptions): Promise<Store> {
  return new Store(opts).open()
}
